import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { HelmetProvider } from "react-helmet-async";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import MobileContactBar from "@/components/MobileContactBar";
import RouteAnalytics from "@/components/RouteAnalytics";
import ChatWidgetTweaks from "@/components/ChatWidgetTweaks";
import { BuyerAuthProvider } from "@/context/BuyerAuthContext";
import { CustomerAuthProvider } from "@/context/CustomerAuthContext";
import { AuthCallback } from "@/components/account/AuthCallback";
import { Toaster } from "@/components/ui/sonner";
import { ROUTES } from "@/routes";

const HEADER_OFFSET = 96;

function scrollToHash(hash) {
  const el = document.getElementById(decodeURIComponent(hash.slice(1)));
  if (!el) return false;
  window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET, behavior: 'smooth' });
  return true;
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    const isAr = pathname === '/ar' || pathname.startsWith('/ar/');
    document.documentElement.lang = isAr ? 'ar' : 'en';
    if (hash) {
      if (!scrollToHash(hash)) {
        const t = setTimeout(() => scrollToHash(hash), 250);
        return () => clearTimeout(t);
      }
      return undefined;
    }
    window.scrollTo(0, 0);
    return undefined;
  }, [pathname, hash]);
  return null;
}

function Layout({ children }) {
  const { pathname } = useLocation();
  if (pathname.startsWith('/auction-admin')) {
    return (<><ScrollToTop />{children}</>);
  }
  if (pathname === '/ai-car-rental/kiosk') {
    return (<><ScrollToTop /><RouteAnalytics />{children}</>);
  }
  return (
    <div className="min-h-screen bg-brand-black">
      <SEO pathname={pathname} />
      <Navigation />
      <ScrollToTop />
      <RouteAnalytics />
      <ChatWidgetTweaks />
      <main className="pt-16 lg:pt-[76px] pb-16 lg:pb-0">{children}</main>
      <Footer />
      <MobileContactBar />
    </div>
  );
}

// Emergent Google OAuth returns to `{origin}/account#session_id=...`; the fragment must be handled
// before any route/protected check runs (read from useLocation, not window.location).
function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) return <AuthCallback />;
  return (
    <Routes>
      {ROUTES.map((r) => <Route key={r.path} path={r.path} element={r.element} />)}
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <BuyerAuthProvider>
          <CustomerAuthProvider>
            <Layout>
              <AppRouter />
            </Layout>
            <Toaster position="top-center" richColors />
          </CustomerAuthProvider>
        </BuyerAuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
